import { Listbox } from "@headlessui/react";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../../../stateManager/store";
import { data } from "../data";
import { ReactComponent as DropdownIcon } from "../../../../../../assets/svg/solobuilder/dropdown.svg";
import { pushFormActionStack, updateElement, updateElementOnUndoRedo } from "../../../../../../stateManager/store/formCreation_v2/actions";
import { getValue, getComponents } from "../../helpers";
import { FormCreationData } from "../../../../../../stateManager/store/formCreation_v2/types";
import { myRedoStack, myUndoStack } from "../../stack/Stack";

type Props = {
  label: string;
  isCheckedIn: boolean;
  defaultValue?: string
}

const excluded_types = [
  ""
]

export const Width = (props: Props) => {
  const { components } = getComponents()
  const { selectedElementId, deviceType, isCoreV3, wrapperComputedStyles, computedStyles } = useSelector((state: AppState) => state.formJson)
  const { isCheckedIn, label } = props;
  const component: FormCreationData = components[selectedElementId];
  const dispatch = useDispatch()

  const widthOptions = data.widthMode ? data.widthMode.map((el) => ({ name: el.label, value: el.value })) : []
  const { type } = component;
  const onChangeMode = (key: string, value: any, name: string) => {
    // Store the current value as oldValue
    const oldValue = {
      width: getValue(component, 'properties.styles.$deviceType.width'.split('.'), deviceType) ?? calculateWidth(),
      widthMode: getValue(component, 'properties.styles.$deviceType.widthMode'.split('.'), deviceType)
    };

    if (key === 'properties.styles.$deviceType.widthMode') {
      myUndoStack.push({
        id: selectedElementId,
        path: key,
        value: oldValue,
        label: "",
        properties: {},
        type: "",
        fields: []
      });
      myRedoStack.clear();
    }

    dispatch(updateElement({
      id: selectedElementId,
      path: key,
      value
    }));

    let widthValue = "";
    if (value === "auto") {
      widthValue = "auto";
    } else if (value === "min-content") {
      widthValue = "min-content";
    } else if (value === "max-content") {
      widthValue = "max-content";
    } else {
      widthValue = value;
    }

    // Determine width mode based on value
    let widthMode = value;
    if (name === "%") {
      widthMode = "%";
    }
    if (name === "px") {
      widthMode = "px";
    }

    // Store the updated value as newValue
    const newValue = {
      width: widthValue,
      widthMode: widthMode
    };

    dispatch(pushFormActionStack({
      undo: () => {
        const poppedElement = myUndoStack.pop();
        if (poppedElement) {

          dispatch(updateElementOnUndoRedo({
            id: poppedElement.id,
            path: 'properties.styles.$deviceType.width',
            value: poppedElement.value.width || "",
            increment: -1
          }));
          dispatch(updateElementOnUndoRedo({
            id: poppedElement.id,
            path: 'properties.styles.$deviceType.widthMode',
            value: poppedElement.value.widthMode || "",
            increment: -1
          }));
          myRedoStack.push({ ...poppedElement, value: newValue });
        }
      },
      redo: () => {
        const poppedElement = myRedoStack.pop();
        if (poppedElement) {
          const oldValue = {
            width: getValue(component, 'properties.styles.$deviceType.width'.split('.'), deviceType) ?? calculateWidth(),
            widthMode: getValue(component, 'properties.styles.$deviceType.widthMode'.split('.'), deviceType)
          };
          dispatch(updateElementOnUndoRedo({
            id: poppedElement.id,
            path: 'properties.styles.$deviceType.width',
            value: poppedElement.value.width,
            increment: 1
          }));
          dispatch(updateElementOnUndoRedo({
            id: poppedElement.id,
            path: 'properties.styles.$deviceType.widthMode',
            value: poppedElement.value.widthMode,
            increment: 1
          }));
          myUndoStack.push({ ...poppedElement, value: oldValue });
        }
      }
    }));
  };

  const onChange = (key: string, value: any) => {
    const oldValue = getValue(component, key.split('.'), deviceType) ? getValue(component, key.split('.'), deviceType) : calculateWidth();
    myUndoStack.push({
      id: selectedElementId,
      path: key,
      value: oldValue,
      label: "",
      properties: {},
      type: "",
      fields: []
    })
    myRedoStack.clear();
    dispatch(updateElement({
      id: selectedElementId,
      path: key,
      value
    }))
    dispatch(pushFormActionStack({
      undo: () => {
        const poppedElement = myUndoStack.pop();
        if (poppedElement) {
          myRedoStack.push({ ...poppedElement, value })
          dispatch(updateElementOnUndoRedo({
            id: poppedElement.id,
            path: poppedElement.path,
            value: poppedElement.value || "",
            increment: -1
          }))
        }
      },
      redo: () => {
        const poppedElement = myRedoStack.pop();
        if (poppedElement) {
          myUndoStack.push({ ...poppedElement, value: oldValue });
          dispatch(updateElementOnUndoRedo({
            id: poppedElement.id,
            path: poppedElement.path,
            value: poppedElement.value,
            increment: 1
          }))
        }
      }
    }))
  }

  const widthMode:string = component.properties.styles[deviceType].widthMode || "px";

  useEffect(() => {
    if (!(component.properties.styles[deviceType].widthMode)) {
      if (type === "Container" || type === "Row" || type === "Grid") {
        dispatch(updateElement({ id: selectedElementId, path: `properties.styles.$deviceType.widthMode`, value: "%" }));
        dispatch(updateElement({ id: selectedElementId, path: `properties.styles.$deviceType.width`, value: "100" }));
      }else{
        dispatch(updateElement({ id: selectedElementId, path: `properties.styles.$deviceType.widthMode`, value: "px" }));
        dispatch(updateElement({ id: selectedElementId, path: `properties.styles.$deviceType.width`, value: component.properties.styles[deviceType].width }));
      }
    }
  }, [deviceType, computedStyles, dispatch, selectedElementId, component]);

  const layout_components = [
    "Row",
    "Column",
    "Grid",
    "Container"
  ]

  const calculateWidth = () => {
    const deviceTypeWidth = getValue(
      component,
      ["properties", "styles", deviceType, "width"],
      deviceType
    );
    if (deviceTypeWidth === undefined) {
      let wrapperStyles: any;
      if (layout_components.includes(type)) {
        wrapperStyles = wrapperComputedStyles
      } else {
        wrapperStyles = computedStyles;
      }
      const width = (wrapperStyles && wrapperStyles.width && wrapperStyles.width.match(/\d+/) ? wrapperStyles.width.match(/\d+/)[0] : null);
      return width
    }
    return deviceTypeWidth
  }

  const getWidthMode = () => {
    const deviceWidthMode = getValue(component, `properties.styles.$deviceType.widthMode`.split("."), deviceType)
    if (!deviceWidthMode) {
      const reg = /([a-z%]+)/
      const wrapperStyles = wrapperComputedStyles.hasOwnProperty("width") ? wrapperComputedStyles : computedStyles;
      const widthMode = (wrapperStyles && wrapperStyles.width && wrapperStyles.width.match(reg) ? wrapperStyles.width.match(reg)[0] : null);
      return widthMode
    }
    return deviceWidthMode
  }

  if (isCoreV3 && excluded_types.includes(type)) {
    return null
  }

  const componentsWithAutoWidth = ["Text",
    "Button",
    "IconButton",
    "Link",
    "Row",
    "Column",
    "Grid",
    "Container",
    "ExpansionPanel",
    "TabBar"
  ];

  const filteredWidthMode = componentsWithAutoWidth.includes(type)
    ? widthOptions
    : widthOptions.filter(option => option.name.toLowerCase() !== "auto");

  return (
    <div className="panel-div" data-test-id={`${type}-${label}`}>
      <label
        htmlFor="width"
        className="panel-label"
      >
        {label}
      </label>
      <div
          style={{ cursor: !isCheckedIn ? "not-allowed" : "auto" }} 
          className="panel-input flex items-center"
      >
        <div className="relative w-1/2 bg-white text-gray-900" style={widthMode === "%" || widthMode === "px" ? { borderRight: "#B4B4B4 solid 1px" } : { width: "100%", paddingLeft: 0 }}>
          <Listbox disabled={!isCheckedIn}
            value={{ name: data.widthMode.find(item => item.value === getWidthMode())?.label ?? data.widthMode[0].label }}
            onChange={(option) => {
              if (data.widthMode) {
                const item = data.widthMode.find(item => item.label === option.name)
                if (item) {
                  onChangeMode(`properties.styles.$deviceType.widthMode`, item.value, option.name)
                  if (option.name === "%" || option.name === "px") {
                    onChangeMode(`properties.styles.$deviceType.width`, "100", option.name)
                  } else {
                    onChangeMode(`properties.styles.$deviceType.width`, item.value, option.name)
                  }
                }
              }
            }}>
            <Listbox.Button data-test-id={`${type}-width_mode-button`} className="flex w-full cursor-pointer rounded-sm bg-white items-center h-full focus:outline-none justify-between">
              <span style={{fontSize:"12px",height:"19px"}}>{data.widthMode.find(item => item.value === getWidthMode())?.label ?? data.widthMode[0].label}</span>
              <span className="pointer-events-none pr-2">
                <DropdownIcon className="h-3 w-3 text-gray-400" />
              </span>
            </Listbox.Button>
            <Listbox.Options data-test-id={`${type}-width_mode-options`} style={{ marginTop: "10px", marginLeft: "-6px", border: "1px solid rgba(125, 132, 146, 0.294)", boxShadow: "0px 5px 8px rgba(125, 132, 146, 0.11)", borderRadius: "4px", width: "170px", maxHeight:"175px", color:"#505f7b", fontSize:"12px", paddingBottom: "10px" }}
            className="absolute max-h-60 w-52 overflow-auto rounded-md bg-white focus:outline-none z-40">
              {filteredWidthMode.map((type, colorIdx) => (
                <Listbox.Option
                  key={colorIdx}
                  style={{ margin: "10px 10px 0px 10px" }}
                  className={({ active }) => `relative cursor-pointer select-none ${active ? "text-selectedText_color":""}`}
                  value={type}
                  data-test-id={`width_mode-${type.name}`}
                >
                  <span className={`block truncate ${widthMode===type.value ? "text-selectedText_color":""}`} style={{lineHeight: "normal"}}>
                    {type.name}
                  </span>
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Listbox></div>
        <br />
        {widthMode === "%" || widthMode === "px" ?
          <input
            style={{ cursor: !isCheckedIn ? "not-allowed" : "auto" }}
            disabled={!isCheckedIn}
            type="text"
            id="width"
            data-test-id={`${type}-${label}-input`}
            className="col-span-2 bg-white text-gray-900 rounded-sm px-3 w-1/2 border-0 h-full outline-none"
            // value={getValue(component, `properties.styles.$deviceType.width`.split("."), deviceType)}
            value={
              isCoreV3 ?
                calculateWidth()
                : getValue(component, `properties.styles.$deviceType.width`.split("."), deviceType)
            }
            onChange={(e: any) => {
              let value = e.target.value
              const regex = /^(\s*|\d+)$/
              const isValid = regex.test(value)
              const item = widthMode === "%"
              if (item && isValid && value > 100) {
                value = 100
              }
              isValid && onChange(`properties.styles.$deviceType.width`, value)
            }}
          /> : <div></div>}
      </div>
    </div >
  )
};
